import { useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const matchFactors = [
  { label: 'Skill alignment', score: 95 },
  { label: 'Industry experience', score: 88 },
  { label: 'Budget fit', score: 92 },
  { label: 'Availability', score: 100 },
  { label: 'Timezone overlap', score: 85 },
];

export default function MatchingEngineSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const scoreCardRef = useRef<HTMLDivElement>(null);
  const progressRefs = useRef<(HTMLDivElement | null)[]>([]);
  const totalScoreRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=130%',
          pin: true,
          scrub: 0.6,
        },
      });

      // Phase 1 (0-30%): Entrance
      // Headline from right
      scrollTl.fromTo(
        headlineRef.current,
        { x: '18vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0
      );

      // Score card from left
      scrollTl.fromTo(
        scoreCardRef.current,
        { x: '-18vw', opacity: 0 },
        { x: 0, opacity: 1, ease: 'none' },
        0.06
      );

      // Progress bars
      progressRefs.current.forEach((bar, index) => {
        if (bar) {
          scrollTl.fromTo(
            bar,
            { scaleX: 0 },
            { scaleX: 1, ease: 'none' },
            0.18 + index * 0.02
          );
        }
      });

      // Total score
      scrollTl.fromTo(
        totalScoreRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, ease: 'none' },
        0.24
      );

      // Phase 2 (30-70%): Settle

      // Phase 3 (70-100%): Exit
      scrollTl.fromTo(
        headlineRef.current,
        { y: 0, opacity: 1 },
        { y: '-12vh', opacity: 0, ease: 'power2.in' },
        0.7
      );

      scrollTl.fromTo(
        scoreCardRef.current,
        { y: 0, opacity: 1 },
        { y: '10vh', x: '6vw', opacity: 0, ease: 'power2.in' },
        0.7
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pinned bg-navy flex items-center z-50"
    >
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Score Card */}
          <div ref={scoreCardRef} className="opacity-0 order-2 lg:order-1">
            <div className="surface-panel p-6 lg:p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white mb-1">
                    Match Analysis
                  </h3>
                  <p className="text-sm text-slate">David Kim vs. your brief</p>
                </div>
                <div
                  ref={totalScoreRef}
                  className="text-right opacity-0"
                >
                  <span className="font-mono text-xs uppercase tracking-wider text-slate block mb-1">
                    Match score
                  </span>
                  <span className="font-heading text-4xl font-bold text-teal">
                    92
                  </span>
                </div>
              </div>

              {/* Progress Bars */}
              <div className="space-y-5">
                {matchFactors.map((factor, index) => (
                  <div key={factor.label}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate">{factor.label}</span>
                      <span className="text-sm font-medium text-white">
                        {factor.score}%
                      </span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        ref={(el) => { progressRefs.current[index] = el; }}
                        className="h-full bg-gradient-to-r from-teal to-teal-dark rounded-full origin-left"
                        style={{ width: `${factor.score}%`, transform: 'scaleX(0)' }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-sm text-slate mb-4">
                  Based on skills, industry fit, availability, and budget alignment.
                </p>
                <button 
                  onClick={() => navigate('/register')}
                  className="text-teal text-sm font-medium flex items-center gap-1 hover:underline"
                >
                  View detailed breakdown
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Right - Headline */}
          <div ref={headlineRef} className="opacity-0 order-1 lg:order-2">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
              Smart Matching
            </span>
            <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white mb-6">
              Explainable matching
            </h2>
            <p className="text-body-lg text-slate mb-8 max-w-lg">
              See exactly why a CXO is recommended—skills, industry fit,
              availability, and budget alignment.
            </p>
            <ul className="space-y-4 mb-8">
              {matchFactors.map((factor) => (
                <li key={factor.label} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-teal" />
                  <span className="text-slate">{factor.label}</span>
                </li>
              ))}
            </ul>
            <button 
              onClick={() => navigate('/register')}
              className="btn-primary flex items-center gap-2"
            >
              View sample matches
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
